import React, {useState, useEffect, useMemo, useCallback} from 'react';
import ReactDOM from "react-dom";

import {Modal} from "../modal/Modal";
import {AddToQuote} from "../quote/components/addToQuote";

let cachedBorderContext = null;

function getBorderContext() {
    if (cachedBorderContext) {
        return cachedBorderContext;
    }
    const productSpecs = document.querySelector('.spec-table');
    const productJsonElement = document.querySelector('[data-product-json]');
    if (!productJsonElement) {
        return null;
    }
    const jsonData = JSON.parse(productJsonElement.innerHTML);
    cachedBorderContext = {
        metafieldsData: jsonData.metafields,
        usesVariantLength: productSpecs ? !!productSpecs.querySelector('.variant-length') : false,
        usesProductLength: productSpecs ? !!productSpecs.querySelector('.product-length') : false,
    };
    return cachedBorderContext;
}

function getBorderLengthForVariant(metafieldsData, variantId, context) {
    if (context.usesVariantLength && metafieldsData[variantId]?.length) {
        return parseFloat(metafieldsData[variantId].length, 10);
    }
    if (context.usesProductLength && metafieldsData.product_length) {
        return parseFloat(metafieldsData.product_length, 10);
    }
    if (metafieldsData[variantId]?.length) {
        return parseFloat(metafieldsData[variantId].length, 10);
    }
    if (metafieldsData.product_length) {
        return parseFloat(metafieldsData.product_length, 10);
    }
    return 0;
}

function getVariantBorderLength(metafieldsData, variant, context) {
    if (!metafieldsData.product_length || metafieldsData[variant.id]?.length) {
        const length = metafieldsData[variant.id]?.length;
        if (length != null) {
            return parseFloat(length, 10);
        }
    }
    return getBorderLengthForVariant(metafieldsData, variant.id, context);
}

const calculatePerimeter = (length, width) => {
    return (Number(length) * 2) + (Number(width) * 2);
};

const getExtraBorder = (variantTitle) => {
    return variantTitle.includes('1 ') ? 1 : 0;
};

const calculateNumberOfBorders = (perimeterFeet, borderLengthInches, variantTitle) => {
    console.log('perimeterFeet', perimeterFeet);
    console.log('borderLengthInches', borderLengthInches);
    console.log('variantTitle', variantTitle);
    if (!(perimeterFeet > 0) || !(borderLengthInches > 0)) {
        return 0;
    }
    return Math.ceil(perimeterFeet / (borderLengthInches / 12)) + getExtraBorder(variantTitle);
};

function computeNumBorders({ perimeter, length, width, borderLength, variantTitle }) {
    const p = Number(perimeter);
    const l = Number(length);
    const w = Number(width);
    if (p > 0 && borderLength > 0) {
        return calculateNumberOfBorders(p, borderLength, variantTitle);
    }
    if (l > 0 && w > 0 && borderLength > 0) {
        return calculateNumberOfBorders(calculatePerimeter(l, w), borderLength, variantTitle);
    }
    return 0;
}

function formatQuantityHeading(variantTitle, count) {
    const variantLabel = variantTitle.includes('Title') || variantTitle.includes('1 ')
        ? ''
        : `(${variantTitle}) `;
    return `Miniumum Quantity ${variantLabel}: ${count}`;
}

export const initializeBorderCalculator = () => {
    const el = document.getElementById('border-calculator');
    if (el) {
        ReactDOM.render(React.createElement(borderCalculator), el);
    }
};

export const borderCalculator = () => {
    const context = getBorderContext();
    const metafieldsData = context?.metafieldsData ?? {};

    const [variant, setVariant] = useState(initialVariant);
    const [isPerimeter, setIsPerimeter] = useState(true);
    const [perimeter, setPerimeter] = useState('');
    const [length, setLength] = useState('');
    const [width, setWidth] = useState('');
    const [borderLength, setBorderLength] = useState(() =>
        context ? getBorderLengthForVariant(metafieldsData, initialVariant.id, context) : 0
    );
    const [showModal, setShowModal] = useState(false);

    const variantTitle = variant.title;

    const numBorders = useMemo(
        () => computeNumBorders({ perimeter, length, width, borderLength, variantTitle }),
        [perimeter, length, width, borderLength, variantTitle]
    );

    const quantityHeading = useMemo(() => {
        if (numBorders <= 0 || !borderLength) {
            return '';
        }
        if (isPerimeter && Number(perimeter) > 0) {
            return formatQuantityHeading(variantTitle, numBorders);
        }
        if (!isPerimeter && Number(length) > 0 && Number(width) > 0) {
            return formatQuantityHeading(variantTitle, numBorders);
        }
        return '';
    }, [numBorders, borderLength, isPerimeter, perimeter, length, width, variantTitle]);

    const updateVariant = useCallback((e) => {
        const nextVariant = e.detail.variant;
        setVariant(nextVariant);
        if (context) {
            setBorderLength(getVariantBorderLength(metafieldsData, nextVariant, context));
        }
    }, [context, metafieldsData]);

    useEffect(() => {
        window.addEventListener('variant:changed', updateVariant);
        return () => window.removeEventListener('variant:changed', updateVariant);
    }, [updateVariant]);

    const toggleCalcType = (e) => {
        if (e.target.value === 'length_perimeter-perimeter') {
            setIsPerimeter(true);
            setWidth('');
        } else if (e.target.value === 'length_perimeter-length_width') {
            setPerimeter('');
            setIsPerimeter(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'perimeter') {
            setPerimeter(value);
        } else if (name === 'length') {
            setLength(value);
        } else if (name === 'width') {
            setWidth(value);
        }
    };

    const addToCart = (e) => {
        e.preventDefault();
        const item = {
            id: variant.id,
            quantity: numBorders
        };

        fetch("".concat(window.routes.cartAddUrl, ".js"), {
            body: JSON.stringify(item),
            credentials: 'same-origin',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        }).then(function (response) {
            if (response.ok) {
                document.documentElement.dispatchEvent(new CustomEvent('product:added', {
                    bubbles: true,
                    detail: {
                        variant: variant,
                        quantity: parseInt(numBorders, 10)
                    }
                }));
            }
        });
    };

    return (
        <>
            <div id="border-calc-form" className="form-wrapper calculator-wrapper">
                {/* {formFields.map((field, index) => (
                    <FormField key={field.name} field={field} handleChange={handleChange} isHidden={isHidden} />
                ))} */}

                <p>Accurate measurements of your playground area are essential to ensure you have enough playground borders.</p>

                <div className="form-group ">
                    <label className="control-label" htmlFor="length_width">Select Measurement:</label>

                    <div className="input-wrapper radio-input-wrapper" onChange={toggleCalcType}>
                        <div className="radio-inner"><label htmlFor="length_width0"><input type="radio" id="length_width0" value="length_perimeter-perimeter" name="length_width" defaultChecked /> Enter the Perimeter (Feet)</label></div>
                        <div className="radio-inner"><label htmlFor="length_width1"><input type="radio" id="length_width1" value="length_perimeter-length_width" name="length_width" /> Enter the Length & Width (Feet)</label></div>
                    </div>
                </div>

                <div className="form-inputs">
                    <div className={isPerimeter ? 'form-group' : 'form-group hidden'}>
                        <label className="control-label" htmlFor="perimeter">Linear Ft. of Perimeter</label>
                        <div className="input-wrapper">
                            <input value={perimeter}
                                onChange={handleChange}
                                min={1}
                                type={isPerimeter ? "number" : "hidden"}
                                name="perimeter"
                                id="perimeter"
                                placeholder="Enter the Perimeter"
                                className="form-control input-md focus:ring-2"
                            />
                        </div>
                    </div>

                    <div className={isPerimeter ? 'form-group hidden' : 'form-group length-width'}>
                        <label className="control-label" htmlFor="length">Length</label>
                        <div className="input-wrapper">
                            <input value={length}
                                onChange={handleChange}
                                min={1}
                                type={isPerimeter ? "hidden" : "number"}
                                name="length" id="length"
                                placeholder="Enter the Length"
                                className="form-control input-md focus:ring-2 "
                            />
                        </div>
                    </div>

                    <div className={isPerimeter ? 'form-group hidden' : 'form-group length-width'}>
                        <label className="control-label" htmlFor="width">Width</label>
                        <div className="input-wrapper">
                            <input value={width}
                                onChange={handleChange}
                                min={1}
                                type={isPerimeter ? "hidden" : "number"}
                                name="width" id="width"
                                placeholder="Enter the Width"
                                className="form-control input-md focus:ring-2"
                            />
                        </div>
                    </div>
                </div>

                <div className="answer-container">
                    <h5 className="text-center text-base font-semibold text-white">
                        {quantityHeading}
                    </h5>

                    {/* <h4>
                        {calculateBorders} : {numBorders} : {calculateBorders2}
                    </h4> */}

                    {/* <div className="button-container">
                        <button className="button button--primary" onClick={addToCart}>Add to Cart { numBorders > 0 ? `(${numBorders})` : '' }</button>
                    </div> */}

                    <div className={numBorders === 0 || numBorders == undefined ? 'hidden' : 'button-container'}>
                        <button className='button button--primary' onClick={addToCart}>Add to Cart { numBorders > 0 ? `(${numBorders})` : '' }</button>
                        <AddToQuote quoteQuantity={numBorders} buttonWidth={'50%'} />
                    </div>
                </div>
            </div>

            <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
                <p>Accurate measurements of your playground area are essential to ensure you have enough playground borders.
                   For a standard rectangular play space, just measure the length and width, add those numbers together and multiply by 2.
                   If the area is an unusual shape, your best bet is to measure each side and add them together.</p>
                <div className="quote-modal-buttons">
                    <button className="button button--primary" onClick={addToCart}>Add to Cart</button>
                    <a className="button button--primary" href="/">Close</a>
                </div>
            </Modal>
        </>
    );
};
